import { OfflineDownloadsFolder } from "@/constants/appSettings";
import { ISODateFormat } from "@/constants/global";
import { AttachmentTypeEnum } from "@/enums/attachment";
import { MimeTypesEnum, PresentationPageTypeEnum } from "@/enums/global";
import { OfflineDownloadStatusEnum, OfflineDownloadTypeEnum } from "@/enums/offlineDownload";
import { getAttachmentFileExtension, openAttachment } from "@/methods/attachmentOpener";
import { getAzureUrl, getDateString, showCustomMessage } from "@/methods/utils";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useOfflineDownloadStore } from "@/stores/core/offlineDownloadStore";
import { useServerClockStore } from "@/stores/core/serverClockStore";
import { PresentationPage } from "@/types/global";
import {
  DownloadContentOptions,
  OfflineDownloadFile,
  OfflineDownloadItem,
} from "@/types/offlineDownload";
import { FileTransfer } from "@capacitor/file-transfer";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { storeToRefs } from "pinia";
import { v4 as uuidv4 } from "uuid";
import { Ref } from "vue";

export async function saveContentForOfflineAccess(options: DownloadContentOptions): Promise<void> {
  switch (options.downloadType) {
    case OfflineDownloadTypeEnum.PDF:
      if (options.fileUrl) {
        await savePdfForOfflineAccess(options.fileUrl, options.fileName, options.percentCompleted);
      }
      break;
    case OfflineDownloadTypeEnum.PRESENTATION:
      if (options.presentationPages) {
        await savePresentationForOfflineAccess(
          options.presentationPages,
          options.fileName,
          options.percentCompleted,
        );
      }
      break;
    case OfflineDownloadTypeEnum.IMAGE:
      if (options.fileUrl) {
        await saveImageForOfflineAccess(
          options.fileUrl,
          options.fileName,
          options.percentCompleted,
        );
      }
      break;
  }
}

export async function savePdfForOfflineAccess(
  fileUrl: string,
  displayName: string,
  percentCompleted?: Ref<number>,
): Promise<void> {
  await handleFileDownload(
    displayName,
    OfflineDownloadTypeEnum.PDF,
    1,
    MimeTypesEnum.PDF,
    async (dirPath) => {
      const fileName = `file${getAttachmentFileExtension(fileUrl) || ".pdf"}`;
      const filePath = `${dirPath}/${fileName}`;
      const fileSize = await saveSingleOfflineFile(fileUrl, filePath, percentCompleted);
      return {
        files: [{ RelativePath: fileName, OriginalUrl: fileUrl, FileSizeBytes: fileSize }],
        totalSize: fileSize,
      };
    },
  );
}

export async function savePresentationForOfflineAccess(
  pages: Array<PresentationPage>,
  displayName: string,
  percentCompleted?: Ref<number>,
): Promise<void> {
  await handleFileDownload(
    displayName,
    OfflineDownloadTypeEnum.PRESENTATION,
    pages.length,
    null,
    async (dirPath) => {
      const files: Array<OfflineDownloadFile> = [];
      let totalSize = 0;
      for (let i = 0; i < pages.length; i++) {
        const { PageType, PageUrl } = pages[i];
        const isDownloadable =
          PageType === PresentationPageTypeEnum.IMAGE || PageType === PresentationPageTypeEnum.GIF;
        if (isDownloadable) {
          const ext = getAttachmentFileExtension(PageUrl) || ".jpg";
          const fileName = `page_${String(i + 1).padStart(3, "0")}${ext}`;
          const fileSize = await saveSingleOfflineFile(PageUrl, `${dirPath}/${fileName}`);
          files.push({
            RelativePath: fileName,
            OriginalUrl: PageUrl,
            FileSizeBytes: fileSize,
            PageType,
          });
          totalSize += fileSize;
        } else {
          // VIDEO and EXTERNAL_LINK: keep the URL, no file to download
          files.push({ RelativePath: "", OriginalUrl: PageUrl, FileSizeBytes: 0, PageType });
        }
        if (percentCompleted) {
          percentCompleted.value = ((i + 1) / pages.length) * 100;
        }
      }
      return { files: files, totalSize: totalSize };
    },
  );
}

export async function saveImageForOfflineAccess(
  imageUrl: string,
  displayName: string,
  percentCompleted?: Ref<number>,
): Promise<void> {
  await handleFileDownload(displayName, OfflineDownloadTypeEnum.IMAGE, 1, null, async (dirPath) => {
    const fileExtension = getAttachmentFileExtension(imageUrl) || ".jpg";
    const fileName = `image${fileExtension}`;
    const filePath = `${dirPath}/${fileName}`;
    const fileSize = await saveSingleOfflineFile(imageUrl, filePath, percentCompleted);
    return {
      files: [{ RelativePath: fileName, OriginalUrl: imageUrl, FileSizeBytes: fileSize }],
      totalSize: fileSize,
    };
  });
}

async function handleFileDownload(
  displayName: string,
  type: OfflineDownloadTypeEnum,
  fileCount: number,
  mimeType: string | null,
  doDownload: (
    dirPath: string,
  ) => Promise<{ files: Array<OfflineDownloadFile>; totalSize: number }>,
): Promise<void> {
  const useAppSettings = useAppSettingsStore();
  const { IsNativePlatform } = storeToRefs(useAppSettings);
  if (!IsNativePlatform.value) {
    showCustomMessage({
      messageText: "Offline downloads are only available on the app.",
      messageType: "warning",
    });
    return;
  }
  const offlineDownloadStore = useOfflineDownloadStore();
  if (offlineDownloadStore.isAlreadyDownloaded(displayName, type)) {
    showCustomMessage({
      messageText: `"${displayName}" is already downloaded.`,
      messageType: "tertiary",
    });
    return;
  }
  const downloadId = uuidv4();
  const dirPath = `${OfflineDownloadsFolder}/${downloadId}`;
  const serverClockStore = useServerClockStore();
  const { UpdatedServerClock } = storeToRefs(serverClockStore);
  const downloadDate = getDateString(UpdatedServerClock.value, ISODateFormat);
  offlineDownloadStore.addDownload({
    Id: downloadId,
    Name: displayName,
    Type: type,
    Status: OfflineDownloadStatusEnum.DOWNLOADING,
    Files: [],
    DirectoryPath: dirPath,
    DownloadedOn: downloadDate,
    TotalSizeBytes: 0,
    MimeType: mimeType,
    FileCount: fileCount,
  });
  try {
    await ensureDirectoryExists(dirPath);
    const { files, totalSize } = await doDownload(dirPath);
    offlineDownloadStore.updateDownloadStatus(downloadId, OfflineDownloadStatusEnum.COMPLETED);
    offlineDownloadStore.updateDownloadSize(downloadId, totalSize);
    const download = offlineDownloadStore.getDownloadById(downloadId);
    if (download) download.Files = files;
    showCustomMessage({
      messageText: `"${displayName}" saved for offline.`,
      messageType: "success",
    });
  } catch (error) {
    console.error("Offline download failed:", error);
    offlineDownloadStore.updateDownloadStatus(downloadId, OfflineDownloadStatusEnum.FAILED);
    showCustomMessage({
      messageText: `Failed to download "${displayName}".`,
      messageType: "danger",
    });
  }
}

export async function openOfflineDownload(download: OfflineDownloadItem): Promise<void> {
  switch (download.Type) {
    case OfflineDownloadTypeEnum.PDF: {
      if (download.Files.length === 0) return;
      const result = await Filesystem.readFile({
        path: `${download.DirectoryPath}/${download.Files[0].RelativePath}`,
        directory: Directory.Data,
      });
      await openAttachment({
        url: `data:${MimeTypesEnum.PDF};base64,${result.data}`,
        type: AttachmentTypeEnum.PDF,
        pdfVariant: "simple",
        extraModalProps: { CustomCssClass: "modal-pdf-viewer" },
      });
      break;
    }
    case OfflineDownloadTypeEnum.PRESENTATION: {
      const pages: Array<PresentationPage> = [];
      for (const file of download.Files) {
        const pageType = file.PageType ?? PresentationPageTypeEnum.IMAGE;
        const isDownloadable =
          pageType === PresentationPageTypeEnum.IMAGE || pageType === PresentationPageTypeEnum.GIF;
        if (isDownloadable) {
          const dataUrl = await readImageFileAsDataUrl(
            `${download.DirectoryPath}/${file.RelativePath}`,
          );
          pages.push({ PageType: pageType, PageUrl: dataUrl });
        } else {
          pages.push({ PageType: pageType, PageUrl: file.OriginalUrl });
        }
      }
      const useAppSettings = useAppSettingsStore();
      const { DeviceControls } = storeToRefs(useAppSettings);
      DeviceControls.value.AllowFullscreen = true;
      DeviceControls.value.AllowScreenRotate = true;
      DeviceControls.value.AllowScreenCast = true;
      await openAttachment({
        url: "",
        name: download.Name,
        type: AttachmentTypeEnum.PRESENTATION_PAGES,
        presentationPages: pages,
        extraModalProps: { CustomCssClass: "modal-presentation-viewer" },
      });
      break;
    }
    case OfflineDownloadTypeEnum.IMAGE: {
      if (download.Files.length === 0) return;
      const file = download.Files[0];
      const dataUrl = await readImageFileAsDataUrl(
        `${download.DirectoryPath}/${file.RelativePath}`,
      );
      await openAttachment({
        url: dataUrl,
        name: download.Name,
        type: AttachmentTypeEnum.IMAGE,
        stacking: "default",
        modalTitle: "Image Viewer",
      });
      break;
    }
  }
}

async function readImageFileAsDataUrl(filePath: string): Promise<string> {
  const result = await Filesystem.readFile({ path: filePath, directory: Directory.Data });
  const ext = getAttachmentFileExtension(filePath);
  const mimeType =
    ext === ".png" ? MimeTypesEnum.PNG : ext === ".gif" ? MimeTypesEnum.GIF : MimeTypesEnum.JPEG;
  return `data:${mimeType};base64,${result.data}`;
}

async function saveSingleOfflineFile(
  url: string,
  localPath: string,
  percentCompleted?: Ref<number>,
): Promise<number> {
  const fullUrl = getAzureUrl(url);
  const fileInfo = await Filesystem.getUri({ directory: Directory.Data, path: localPath });
  const listenerHandle = await FileTransfer.addListener("progress", (progress) => {
    if (percentCompleted && progress.lengthComputable) {
      percentCompleted.value = (progress.bytes / progress.contentLength) * 100;
    }
  });
  try {
    await FileTransfer.downloadFile({ url: fullUrl, path: fileInfo.uri, progress: true });
  } finally {
    await listenerHandle.remove();
  }
  const stat = await Filesystem.stat({ path: localPath, directory: Directory.Data });
  return stat.size;
}

async function ensureDirectoryExists(dirPath: string): Promise<void> {
  try {
    await Filesystem.mkdir({ path: dirPath, directory: Directory.Data, recursive: true });
  } catch (error: any) {
    if (!error?.message?.includes("exists")) {
      throw error;
    }
  }
}

export async function removeDataDirectory(path: string): Promise<void> {
  const exists = await Filesystem.stat({ path, directory: Directory.Data })
    .then(() => true)
    .catch(() => false);
  if (!exists) {
    return;
  }
  try {
    await Filesystem.rmdir({ path, directory: Directory.Data, recursive: true });
  } catch (error) {
    console.warn("Error removing offline download directory:", path, error);
  }
}
